const STORAGE_KEY = 'hydrotools_cognito_tokens_v1';
const PKCE_VERIFIER_KEY = 'hydrotools_cognito_pkce_verifier_v1';

function getConfig() {
  const domain = String(import.meta.env.VITE_COGNITO_DOMAIN || '').trim().replace(/\/+$/, '');
  const clientId = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
  const region = String(import.meta.env.VITE_COGNITO_REGION || '').trim();
  const redirectUri = String(
    import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`
  ).trim();
  const signoutUri = String(
    import.meta.env.VITE_COGNITO_SIGNOUT_URI || `${window.location.origin}/`
  ).trim();

  const missing = [];
  if (!domain) missing.push('VITE_COGNITO_DOMAIN');
  if (!clientId) missing.push('VITE_COGNITO_CLIENT_ID');
  if (!region) missing.push('VITE_COGNITO_REGION');
  if (missing.length) {
    throw new Error(`Missing Cognito configuration: ${missing.join(', ')}`);
  }

  return { domain, clientId, region, redirectUri, signoutUri };
}

function parseJwt(token) {
  const payload = token.split('.')[1];
  if (!payload) return null;
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

function tokensAreValid(tokens) {
  if (!tokens?.id_token || !tokens?.access_token) return false;
  try {
    const claims = parseJwt(tokens.id_token);
    const exp = Number(claims?.exp || 0);
    return Number.isFinite(exp) && (Date.now() / 1000) < (exp - 30);
  } catch {
    return false;
  }
}

function getSavedTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return tokensAreValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// Authenticated API modules read a fresh token for each request; tokens are never
// included in project exports, conversation histories, or tool arguments.
export function getIdToken() {
  return getSavedTokens()?.id_token || null;
}

function saveTokens(tokens) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

async function sha256(buffer) {
  return window.crypto.subtle.digest('SHA-256', buffer);
}

function toBase64Url(uint8) {
  let binary = '';
  for (let i = 0; i < uint8.length; i += 1) binary += String.fromCharCode(uint8[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

async function startLogin(config) {
  const verifier = randomString(64);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challengeBytes = await sha256(new TextEncoder().encode(verifier));
  const challenge = toBase64Url(new Uint8Array(challengeBytes));
  const state = randomString(24);
  const scope = encodeURIComponent('openid email profile');
  const loginUrl =
    `${config.domain}/login?response_type=code` +
    `&client_id=${encodeURIComponent(config.clientId)}` +
    `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
    `&scope=${scope}` +
    `&state=${encodeURIComponent(state)}` +
    `&code_challenge_method=S256` +
    `&code_challenge=${encodeURIComponent(challenge)}`;
  window.location.assign(loginUrl);
}

async function exchangeCodeForTokens(config, code) {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) {
    const cleanPath = `${window.location.pathname}`;
    window.history.replaceState({}, '', cleanPath);
    await startLogin(config);
    return null;
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  const resp = await fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!resp.ok) {
    const detail = await resp.text();
    throw new Error(`Token exchange failed (${resp.status}): ${detail}`);
  }
  const tokens = await resp.json();
  if (!tokensAreValid(tokens)) throw new Error('Received invalid Cognito tokens.');
  saveTokens(tokens);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  window.history.replaceState({}, '', '/');
  return tokens;
}

export async function ensureAuthenticated() {
  const config = getConfig();
  const existing = getSavedTokens();
  if (existing) return existing;

  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const atCallbackPath = url.pathname === new URL(config.redirectUri).pathname;

  if (code && atCallbackPath) {
    return exchangeCodeForTokens(config, code);
  }

  await startLogin(config);
  return null;
}

export function getAuthContext() {
  const tokens = getSavedTokens();
  if (!tokens?.id_token) return { groups: [] };
  try {
    const claims = parseJwt(tokens.id_token) || {};
    const groupsRaw = claims["cognito:groups"];
    const groups = Array.isArray(groupsRaw) ? groupsRaw.map((g) => String(g)) : [];
    return {
      sub: String(claims.sub || ""),
      email: String(claims.email || ""),
      groups,
      claims,
    };
  } catch {
    return { groups: [] };
  }
}

export function addLogoutButton() {
  const topbar = document.getElementById('topbar');
  if (!topbar || document.getElementById('logout-btn')) return;
  const config = getConfig();
  const button = document.createElement('button');
  button.id = 'logout-btn';
  button.type = 'button';
  button.textContent = 'Log out';
  button.style.marginLeft = '8px';
  button.style.padding = '6px 10px';
  button.style.border = '1px solid #3e4f60';
  button.style.borderRadius = '6px';
  button.style.background = '#11263b';
  button.style.color = '#e9f1fa';
  button.style.cursor = 'pointer';
  button.addEventListener('click', () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    const logoutUrl =
      `${config.domain}/logout?client_id=${encodeURIComponent(config.clientId)}` +
      `&logout_uri=${encodeURIComponent(config.signoutUri)}`;
    window.location.assign(logoutUrl);
  });
  topbar.appendChild(button);
}
